import React from 'react';
import { useTranslation } from 'react-i18next';
import { InputGroup } from '../../common/InputGroup';
import { DropdownMultiTypeahead } from '../../common/DropdownMultiTypeahead';
import { ProfileImageSection } from './ProfileImageSection';
import { PersonalInfoSection } from './PersonalInfoSection';
import { BaseFormLayout } from './BaseFormLayout';
import { City } from '../../../types/profile';
import { COMPANY_TYPES, CompanyType } from '../../../types/profile';
import { RadioButton } from '../../common/RadioButton';
import { Industry } from '../../../types/industry';
import FormLanguageSelector from '../../common/FormLanguageSelector';
import { toast } from 'react-toastify';
import { useProfileStore } from '../../../stores/profileStore';
import { useIndustrySelector } from '../../../hooks/useIndustrySelector';

interface RecruiterFormProps {
  firstName: string;
  lastName: string;
  phone: string;
  cityQuery: string;

  companyType: CompanyType;
  companyName?: string;
  jobTitle: string | undefined;
  organizationWebsite?: string;
  organizationNumber?: string;
  languages: string[];
  selectedIndustries: Industry[];
  errors: {
    firstName?: string;
    lastName?: string;
    city?: string;
    phone?: string;
    industries?: string;
    companyName?: string;
    jobTitle?: string;
    organizationIdentity?: string;
    languages?: string;
  };
  onFirstNameChange: (value: string) => void;
  onLastNameChange: (value: string) => void;
  onPhoneChange: (value: string) => void;
  onCitySelect: (city: City | null) => void;

  onCompanyNameChange: (value: string) => void;
  onJobTitleChange: (value: string) => void;
  onOrganizationWebsiteChange: (value: string) => void;
  onOrganizationNumberChange: (value: string) => void;
  onIndustriesChange: (industries: Industry[]) => void;
  onCompanyTypeChange: (type: CompanyType) => void;
  onLanguagesChange: (languages: string[]) => void;
  cityOptions: City[];
  onBack?: () => void;
  onSubmit: (e: React.FormEvent) => void;
  isSubmitting: boolean;
}

export const RecruiterForm: React.FC<RecruiterFormProps> = ({
  firstName,
  lastName,
  phone,
  cityQuery,

  companyType,
  companyName,
  jobTitle,
  organizationWebsite,
  organizationNumber,
  selectedIndustries,
  languages,
  errors,
  cityOptions,
  onFirstNameChange,
  onLastNameChange,
  onPhoneChange,
  onCitySelect,

  onCompanyNameChange,
  onJobTitleChange,
  onOrganizationWebsiteChange,
  onOrganizationNumberChange,
  onIndustriesChange,
  onCompanyTypeChange,
  onLanguagesChange,
  onBack,
  onSubmit,
  isSubmitting,
}) => {
  const { t } = useTranslation('auth');

  const { suggestNewIndustry } = useProfileStore();

  const renderCompanyFields = () => {
    if (companyType === COMPANY_TYPES.COMPANY || companyType === COMPANY_TYPES.ORGANIZATION) {
      return (
        <>
          <InputGroup
            label={t('companyName')}
            value={companyName || ''}
            onChange={e => onCompanyNameChange(e.target.value)}
            placeholder={t('companyNamePlaceholder')}
            required
            error={!!errors.companyName}
            errorMessage={errors.companyName}
          />
          <InputGroup
            label={t('jobTitle')}
            value={jobTitle || ''}
            onChange={e => onJobTitleChange(e.target.value)}
            placeholder={t('jobTitlePlaceholder')}
            required={companyType === COMPANY_TYPES.ORGANIZATION}
            error={!!errors.jobTitle}
            errorMessage={errors.jobTitle}
          />
          {companyType === COMPANY_TYPES.ORGANIZATION && (
            <>
              <InputGroup
                label={t('organizationWebsite')}
                value={organizationWebsite || ''}
                onChange={e => onOrganizationWebsiteChange(e.target.value)}
                placeholder={t('organizationWebsitePlaceholder')}
                error={!!errors.organizationIdentity}
                errorMessage={errors.organizationIdentity}
              />
              <InputGroup
                label={t('organizationNumber')}
                value={organizationNumber || ''}
                onChange={e => onOrganizationNumberChange(e.target.value)}
                placeholder={t('organizationNumberPlaceholder')}
                error={!!errors.organizationIdentity}
                errorMessage={errors.organizationIdentity}
              />
              <p className="mt-1 text-sm text-gray-500">{t('organizationIdentityHint')}</p>
            </>
          )}
        </>
      );
    }
    return null;
  };

  const renderCompanyTypeSelector = () => {
    return (
      <div className="mb-8 flex space-x-8">
        <RadioButton
          checked={companyType === COMPANY_TYPES.INDIVIDUAL}
          onChange={() => onCompanyTypeChange(COMPANY_TYPES.INDIVIDUAL)}
          label={t('common:companyType.individual')}
          name="companyType"
          value={COMPANY_TYPES.INDIVIDUAL}
        />
        <RadioButton
          checked={companyType === COMPANY_TYPES.COMPANY}
          onChange={() => onCompanyTypeChange(COMPANY_TYPES.COMPANY)}
          label={t('common:companyType.company')}
          name="companyType"
          value={COMPANY_TYPES.COMPANY}
        />
        <RadioButton
          checked={companyType === COMPANY_TYPES.ORGANIZATION}
          onChange={() => onCompanyTypeChange(COMPANY_TYPES.ORGANIZATION)}
          label={t('common:companyType.organization')}
          name="companyType"
          value={COMPANY_TYPES.ORGANIZATION}
        />
      </div>
    );
  };

  const { filteredOptions: industryOptions, setSearchInput: setIndustrySearchInput } =
    useIndustrySelector(selectedIndustries);

  const handleIndustryAdded = (industry: Industry) => {
    onIndustriesChange([...selectedIndustries, industry]);
  };

  const handleCreateNewIndustry = async (query: string) => {
    try {
      const newIndustry = await suggestNewIndustry({
        label: query,
        description: '',
      });

      handleIndustryAdded(newIndustry);

      toast.success(t('industryAddedSuccess', { ns: 'settings', defaultValue: 'Industry added!' }));
    } catch (err) {
      console.error('Error creating industry:', err);
      toast.error(
        t('industryAddError', { ns: 'settings', defaultValue: 'Could not add industry.' })
      );
    }
  };

  return (
    <BaseFormLayout
      firstName={firstName}
      onBack={onBack}
      onSubmit={onSubmit}
      isSubmitting={isSubmitting}
    >
      <div className="font-ambit mb-4 text-[1.5625rem] font-[900] not-italic leading-[1.875rem] text-secondary">
        {t('completeProfileStep.inscription')}
      </div>

      {renderCompanyTypeSelector()}

      {companyType === COMPANY_TYPES.INDIVIDUAL ? (
        <div className="text-[#3B5266] text-[0.9375rem] md:text-[0.875rem] leading-[1.25rem] md:leading-[1.1875rem]">
          <p className="mb-2">{t('completeProfileStep.personalInfoDescriptionTitle')}</p>
          <p className="italic">{t('completeProfileStep.personalInfoDescriptionSubtitle')}</p>
        </div>
      ) : companyType === COMPANY_TYPES.ORGANIZATION ? (
        <div className="text-[#3B5266] text-[0.9375rem] md:text-[0.875rem] leading-[1.25rem] md:leading-[1.1875rem]">
          <p className="mb-2">{t('completeProfileStep.organizationInfoDescriptionTitle')}</p>
          <p className="italic">{t('completeProfileStep.organizationInfoDescriptionSubtitle')}</p>
        </div>
      ) : (
        <div className="text-[#3B5266] text-[0.9375rem] md:text-[0.875rem] leading-[1.25rem] md:leading-[1.1875rem]">
          <p className="mb-2">{t('completeProfileStep.corporateInfoDescriptionTitle')}</p>
          <p className="italic">{t('completeProfileStep.corporateInfoDescriptionSubtitle')}</p>
        </div>
      )}

      <div className="font-ambit mb-4 text-[1.0625rem] font-normal not-italic leading-[1.5625rem] text-secondary">
        {t('completeProfileStep.professionalInfo')}
      </div>

      <ProfileImageSection />

      <PersonalInfoSection
        firstName={firstName}
        lastName={lastName}
        phone={phone}
        cityQuery={cityQuery}
        onFirstNameChange={onFirstNameChange}
        onLastNameChange={onLastNameChange}
        onPhoneChange={onPhoneChange}
        onCitySelect={onCitySelect}
        cityOptions={cityOptions}
        errors={errors}
      />

      {renderCompanyFields()}

      {companyType !== COMPANY_TYPES.INDIVIDUAL && (
        <InputGroup
          label={t('businessIndustries')}
          required
          error={!!errors.industries}
          errorMessage={errors.industries}
        >
          <DropdownMultiTypeahead
            selected={selectedIndustries}
            onChange={onIndustriesChange}
            options={industryOptions}
            onSearchChange={setIndustrySearchInput}
            getOptionLabel={item => item.label}
            getOptionValue={item => item.id}
            placeholder={t('businessIndustriesHint')}
            className="mt-2"
            error={!!errors.industries}
            onCreate={handleCreateNewIndustry}
            inputMode="search"
          />
        </InputGroup>
      )}

      <FormLanguageSelector
        value={languages}
        onChange={onLanguagesChange}
        error={errors.languages}
        required
      />
    </BaseFormLayout>
  );
};
